/**
 * Endpoints del reporte del controlador PTS2 (`/api/pts2_*`) y del monitoreo
 * en vivo (`/api/pump_status`, `/api/tanks_status`, …).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiClient } from '@/lib/http/api-client';

import { aApi } from './lib/pts2-format';
import type {
  ControllerSetup,
  FiltrosRango,
  Pts2Aux,
  Pts2Filtros,
  Pts2Reporte,
  RespuestaConciliacion,
  RespuestaMediciones,
  RespuestaPumpStatus,
  RespuestaTanksStatus,
} from './monitoreo.types';

/** Cadencia de refresco de las pantallas en vivo. */
const INTERVALO_LADOS_MS = 3_000;
const INTERVALO_TANQUES_MS = 5_000;

export const monitoreoKeys = {
  all: ['monitoreo'] as const,
  pts2Aux: ['monitoreo', 'pts2-aux'] as const,
  pts2Reporte: (filtros: Pts2Filtros) => ['monitoreo', 'pts2-reporte', filtros] as const,
  setup: ['monitoreo', 'controller-setup'] as const,
  lados: (lados: number[]) => ['monitoreo', 'pump-status', lados.join(',')] as const,
  tanques: ['monitoreo', 'tanks-status'] as const,
  conciliacion: (filtros: FiltrosRango) => ['monitoreo', 'conciliacion', filtros] as const,
  mediciones: (filtros: FiltrosRango) => ['monitoreo', 'mediciones', filtros] as const,
} as const;

export const monitoreoApi = {
  pts2Auxiliares: (signal?: AbortSignal): Promise<Pts2Aux> =>
    apiClient.get<Pts2Aux>('/api/pts2_aux', { signal }),

  pts2Reporte: (filtros: Pts2Filtros, signal?: AbortSignal): Promise<Pts2Reporte> =>
    apiClient.get<Pts2Reporte>('/api/pts2_report', {
      query: {
        lados: filtros.lados.join(','),
        productos: filtros.productos.join(','),
        fecha_inicio: aApi(filtros.fecha_inicio),
        fecha_fin: aApi(filtros.fecha_fin),
        comprobantes: filtros.comprobantes,
      },
      signal,
    }),

  /** Lados, mangueras y tanques configurados (se cachea: casi no cambia). */
  setup: (signal?: AbortSignal): Promise<ControllerSetup> =>
    apiClient.get<ControllerSetup>('/api/controller_setup', { signal }),

  /** Estado en vivo de los lados. `sincronizar` guarda el último comprobante. */
  pumpStatus: (lados: number[], signal?: AbortSignal): Promise<RespuestaPumpStatus> =>
    apiClient.get<RespuestaPumpStatus>('/api/pump_status', {
      query: { sides: lados.join(',') },
      signal,
    }),

  /** Parada de emergencia de uno o varios lados (sólo administradores). */
  paradaEmergencia: (lados: number[]): Promise<{ message: string }> =>
    apiClient.post<{ message: string }>('/api/pump_emergency_stop', {
      body: { lados },
    }),

  /** Telemetría en vivo de los tanques configurados. */
  tanksStatus: (signal?: AbortSignal): Promise<RespuestaTanksStatus> =>
    apiClient.get<RespuestaTanksStatus>('/api/tanks_status', { signal }),

  /** Conciliación surtidor / tanque del período. */
  conciliacion: (filtros: FiltrosRango, signal?: AbortSignal): Promise<RespuestaConciliacion> =>
    apiClient.get<RespuestaConciliacion>('/api/tank_conciliacion', {
      query: {
        tank: filtros.tank,
        fecha_inicio: aApi(filtros.fecha_inicio),
        fecha_fin: aApi(filtros.fecha_fin),
      },
      signal,
    }),

  /** Mediciones y entregas de un tanque en el período. */
  mediciones: (filtros: FiltrosRango, signal?: AbortSignal): Promise<RespuestaMediciones> =>
    apiClient.get<RespuestaMediciones>('/api/tank_mediciones', {
      query: {
        tank: filtros.tank,
        fecha_inicio: aApi(filtros.fecha_inicio),
        fecha_fin: aApi(filtros.fecha_fin),
      },
      signal,
    }),
} as const;

/** Lados y productos para los filtros del reporte. */
export function usePts2Auxiliares(enabled = true) {
  return useQuery({
    queryKey: monitoreoKeys.pts2Aux,
    queryFn: ({ signal }) => monitoreoApi.pts2Auxiliares(signal),
    enabled,
    staleTime: 10 * 60_000,
  });
}

/** Reporte de transacciones del controlador. */
export function usePts2Reporte(filtros: Pts2Filtros, enabled = true) {
  return useQuery({
    queryKey: monitoreoKeys.pts2Reporte(filtros),
    queryFn: ({ signal }) => monitoreoApi.pts2Reporte(filtros, signal),
    enabled,
    staleTime: 60_000,
  });
}

/** Configuración de lados, mangueras y tanques. */
export function useControllerSetup(enabled = true) {
  return useQuery({
    queryKey: monitoreoKeys.setup,
    queryFn: ({ signal }) => monitoreoApi.setup(signal),
    enabled,
    staleTime: 10 * 60_000,
  });
}

/**
 * Estado en vivo de los lados.
 *
 * Se refresca cada 3 s (el panel anterior lo hacía cada 500 ms) y sólo mientras
 * la pestaña está visible.
 */
export function usePumpStatus(lados: number[], enabled = true) {
  return useQuery({
    queryKey: monitoreoKeys.lados(lados),
    queryFn: ({ signal }) => monitoreoApi.pumpStatus(lados, signal),
    enabled: enabled && lados.length > 0,
    refetchInterval: INTERVALO_LADOS_MS,
    staleTime: 0,
  });
}

/** Telemetría en vivo de los tanques, cada 5 s. */
export function useTanksStatus(enabled = true) {
  return useQuery({
    queryKey: monitoreoKeys.tanques,
    queryFn: ({ signal }) => monitoreoApi.tanksStatus(signal),
    enabled,
    refetchInterval: INTERVALO_TANQUES_MS,
    staleTime: 0,
  });
}

/** Conciliación surtidor / tanque del período seleccionado. */
export function useConciliacion(filtros: FiltrosRango, enabled = true) {
  return useQuery({
    queryKey: monitoreoKeys.conciliacion(filtros),
    queryFn: ({ signal }) => monitoreoApi.conciliacion(filtros, signal),
    enabled: enabled && Boolean(filtros.fecha_inicio) && Boolean(filtros.fecha_fin),
    staleTime: 60_000,
  });
}

/** Mediciones y entregas del tanque en el período seleccionado. */
export function useMedicionesTanque(filtros: FiltrosRango, enabled = true) {
  return useQuery({
    queryKey: monitoreoKeys.mediciones(filtros),
    queryFn: ({ signal }) => monitoreoApi.mediciones(filtros, signal),
    enabled:
      enabled && Boolean(filtros.tank) && Boolean(filtros.fecha_inicio) && Boolean(filtros.fecha_fin),
    staleTime: 60_000,
  });
}

/** Parada de emergencia de los lados indicados. */
export function useParadaEmergencia() {
  const cliente = useQueryClient();

  return useMutation({
    mutationFn: (lados: number[]) => monitoreoApi.paradaEmergencia(lados),
    onSuccess: () => cliente.invalidateQueries({ queryKey: monitoreoKeys.all }),
  });
}
